import TableView from 'mini-mail-list-store/lib/store/schema/TableView';
import {
    getLastDeliveryOrRenewTimeStamp,
    getLastDeliveryTimeStamp,
} from 'mini-mail-list-store/lib/selectors/mailListDataPropertyGetter';
import { shouldTableSortByRenewTime } from '../../../utils/tables/shouldFolderSortByRenewTime';

/**
 * Compare the date time of two rows
 * @param targetLastDeliveryOrRenewTimeStamp the lastDeliveryOrRenewTimeStamp of the conversation to insert
 * @param targetLastDeliveryTimeStamp the lastDeliveryTimeStamp of the conversation to insert
 * @param idOfReferenceRow the conversation id of the reference row
 * @param tableView the table view
 * @returns
 * 1) < 0 if reference row is earlier than target row
 * 2) 0 if have the same time
 * 3) > 0 if reference row is later than target row
 */
function compareRowDateTime(
    targetLastDeliveryOrRenewTimeStamp: string,
    targetLastDeliveryTimeStamp: string,
    idOfReferenceRow: string,
    tableView: TableView
): number {
    const lastDeliveryTimeStamp = getLastDeliveryTimeStamp(idOfReferenceRow, tableView);
    const lastDeliveryOrRenewTimeStamp = getLastDeliveryOrRenewTimeStamp(
        idOfReferenceRow,
        tableView
    );
    const diffDeliveryUTCTime =
        new Date(lastDeliveryTimeStamp).getTime() - new Date(targetLastDeliveryTimeStamp).getTime();

    if (shouldTableSortByRenewTime(tableView.tableQuery)) {
        // If table supports pin, use renew time as primary sort, and delivery time as secondary sort
        const diffRenewTimeInUTC =
            new Date(lastDeliveryOrRenewTimeStamp).getTime() -
            new Date(targetLastDeliveryOrRenewTimeStamp).getTime();
        return diffRenewTimeInUTC !== 0 ? diffRenewTimeInUTC : diffDeliveryUTCTime;
    } else {
        return diffDeliveryUTCTime;
    }
}

/**
 * Computes the insert index for the row in date view sort
 * @param lastDeliveryOrRenewTimeStamp the lastDeliveryOrRenewTimeStamp of the conversation to insert
 * @param lastDeliveryTimeStamp the lastDeliveryTimeStamp of the conversation to insert
 * @param startIndex the start index to scan from. If we're pinning, start from top, if unpinning, it can only be moved to at or below the old index
 * @returns insert index for the row, -1 if could not place the row
 */
export default function findIndexToInsertAt(
    lastDeliveryOrRenewTimeStamp: string,
    lastDeliveryTimeStamp: string,
    tableView: TableView,
    startIndex: number
): number {
    // Handle the case when tableView is empty
    // We will insert this item at index 0 to begin
    if (tableView.itemIds.length === 0) {
        return 0;
    }
    // React OWA only supports date sort in descending order
    // Scan from top until we find row that's earlier than the row
    for (let i = startIndex; i < tableView.itemIds.length; i++) {
        if (
            compareRowDateTime(
                lastDeliveryOrRenewTimeStamp,
                lastDeliveryTimeStamp,
                tableView.itemIds[i],
                tableView
            ) <= 0
        ) {
            return i;
        }
    }

    // There is no row in table that's earlier than the row to insert. This could happen if user unpins
    // an old conversation
    if (tableView.itemIds.length === tableView.totalRowsInView) {
        // Insert the row to the end of the table in case client has loaded all rows from server.
        return tableView.itemIds.length - 1;
    } else {
        // Otherwise returns -1 as we couldn't find an index to insert the conversation
        return -1;
    }
}
